import { COLLECTION_KINDS, KIND_METADATA, type CollectionKind } from "@atelier/config";
import { cn } from "@/lib/cn";

/**
 * "pfp" is a virtual filter: PFP-derived collections are stored with
 * kind="drop" in the API but carry a pfpProjectId. The toolbar treats it
 * as a peer of the real kinds so the user can narrow to just procedural
 * collections without seeing curated Drops mixed in.
 */
export type KindFilterValue = "all" | CollectionKind | "pfp";

export const KIND_FILTER_VALUES: KindFilterValue[] = [
  "all",
  ...COLLECTION_KINDS,
  "pfp",
];

const VIRTUAL_LABELS: Record<KindFilterValue, string> = {
  all: "All",
  drop: "Drops",
  simple: "Simple",
  badge: "Badges",
  "1of1": "1/1",
  pfp: "PFPs",
};

export function kindFilterLabel(v: KindFilterValue): string {
  return VIRTUAL_LABELS[v] ?? KIND_METADATA[v as CollectionKind]?.pluralLabel ?? v;
}

/**
 * Applies the chip filter client-side. The API can scope by `kind` directly,
 * but the "pfp" virtual filter requires post-filtering on `pfpProjectId`,
 * and "drop" should exclude PFP-derived collections so the two are mutually
 * exclusive in the UI.
 */
export function matchesKindFilter<T extends { kind: CollectionKind; pfpProjectId?: string | null }>(
  item: T,
  filter: KindFilterValue,
): boolean {
  if (filter === "all") return true;
  if (filter === "pfp") return !!item.pfpProjectId;
  if (filter === "drop") return item.kind === "drop" && !item.pfpProjectId;
  return item.kind === filter;
}

export function KindFilter({
  value,
  onChange,
  counts,
}: {
  value: KindFilterValue;
  onChange: (v: KindFilterValue) => void;
  counts?: Partial<Record<KindFilterValue, number>>;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5" role="tablist" aria-label="Filter collections by kind">
      {KIND_FILTER_VALUES.map((v) => {
        const active = value === v;
        const count = counts?.[v];
        return (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(v)}
            className={cn(
              "rounded-full border px-2.5 py-1 text-xs font-medium transition",
              active
                ? "border-foreground bg-foreground text-background"
                : "border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground",
            )}
          >
            {kindFilterLabel(v)}
            {typeof count === "number" && (
              <span className="ml-1 text-[10px] tabular-nums opacity-70">{count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
